import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from "../../app/store";
import {
    clearError,
    continueGameSession, endGameSession,
    pauseGameSession,
    questionAnswer, sessionIsAlive, setEndFormData,
    startGameSession
} from "../../app/slices/gameSession";
import QuestionContent from "./QuestionContent";
import {QuestionTypeConnect, QuestionTypeOrder, QuestionTypeSelect, QuestionTypeText} from "../../types/gameSession";
import { Language, translations } from "../../translations";
import toast from "react-hot-toast";
import GameSlide from "./GameSlide";
import { ReactComponent as InfoIcon } from '../../images/icons/circle-info-regular.svg';
import { CSSTransition } from 'react-transition-group';
import { TRANSITION_DURATION } from './Game';
import CircleProgress from './CircleProgress';
import FormFields from "./FormFields";

type Props = {
    game_id: number;
    lang: Language;
}

const GameSession = ({ game_id, lang }: Props) => {
    const dispatch = useAppDispatch();
    const {
        session_data,
        loaded,
        error,
        transition,
        showPointsDiff,
        redirectUrl
    } = useAppSelector((state) => state.gameSession);
    const { data } = useAppSelector((state) => state.game);
    const LANG = translations[lang];
    const [isVisible, setIsVisible] = React.useState(false);
    const [slideTransition, setSlideTransition] = React.useState<"slide-right" | "slide-left">("slide-right");
    const [nextBtnClassName, setNextBtnClassName] = React.useState('circle-button pulse active');
    const transRef = React.useRef(null);

    const btnDisabled = !loaded || transition;

    useEffect(() => {
        // NOTE: on React.StrictMode, this useEffect is called twice on dev mode
        if (loaded) return;
        const form_fields = data?.form;
        dispatch(startGameSession({ form_fields, game_id: game_id, language: lang }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded]);

    // call setInterval every 3 minutes to keep the session alive
    useEffect(() => {
        const interval = setInterval(() => {
            if (typeof (session_data) !== "undefined") {
                dispatch(sessionIsAlive({ game_session_id: session_data.id }));
            }
        }, 3 * 60 * 1000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // pause the game session when the user closes the tab
    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (typeof (session_data) !== "undefined") {
                dispatch(pauseGameSession({ game_session_id: session_data.id }));
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (redirectUrl !== null) {
            window.location.href = redirectUrl;
        }
    }, [redirectUrl]);

    const questionOptions = (session_data?.question as QuestionTypeSelect)?.options ?? [];

    useEffect(() => {
        // trigger pulse animation when user changes the selected options
        if (session_data?.question?.type === 'select') {
            setNextBtnClassName('circle-button active');
            setTimeout(() => {
                setNextBtnClassName('circle-button pulse active');
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(questionOptions)]);


    useEffect(() => {
        if (loaded && !transition) {
            window.scrollTo({ top: 1, behavior: 'instant' });
        }
        setIsVisible(loaded && !transition);
    }, [loaded, transition]);

    const handleContinue = () => {
        if (session_data?.content_id !== undefined) {
            setSlideTransition("slide-right");
            dispatch(continueGameSession({ content_id: session_data.content_id, back: false }))
        }
    }

    const handleBack = () => {
        if (session_data?.content_id !== undefined) {
            setSlideTransition("slide-left");
            dispatch(continueGameSession({ content_id: session_data.content_id, back: true }))
        }
    }

    let questionInfo = '';
    switch (session_data?.question?.type) {
        case 'select':
            const question = session_data.question as QuestionTypeSelect;
            if (question.multiple) {
                questionInfo = LANG.select_multiple_info;
            } else {
                questionInfo = LANG.select_single_info;
            }
            break;
        case 'order':
            questionInfo = LANG.order_info;
            break;
        case 'connect':
            questionInfo = LANG.connect_info;
            break;
        default:
            questionInfo = '';
    }

    const handleAnswer = () => {
        let payload: any = {
            content_id: session_data?.content_id,
        }

        setSlideTransition("slide-right");

        switch (session_data?.question?.type) {
            case 'select':
                const question = session_data.question as QuestionTypeSelect;
                const selectedIndexes = question.options
                    .filter(option => option.selected)
                    .map(option => option.index);
                payload.answers = selectedIndexes;
                dispatch(questionAnswer(payload));
                break;
            case 'order':
                const orderQuestion = session_data.question as QuestionTypeOrder;
                payload.answers = orderQuestion.options.map(option => option.index);
                dispatch(questionAnswer(payload));
                break;
            case 'connect':
                const connectQuestion = session_data.question as QuestionTypeConnect;
                payload.answers = connectQuestion.selected_pairs;
                dispatch(questionAnswer(payload));
                break;
            case 'text':
                const textQuestion = session_data.question as QuestionTypeText;
                payload.answers = textQuestion.options;
                dispatch(questionAnswer(payload));
                break;
            default:
                if (session_data?.slide) {
                    dispatch(questionAnswer(payload));
                }
        }
    };


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;
        dispatch(setEndFormData({[name]: value}));
    }

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, checked} = e.target;
        dispatch(setEndFormData({[name]: checked ? '1' : ''}));
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        console.log('why?')
    }

    const handleEndFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const form = e.currentTarget as HTMLFormElement;

        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        const formData = new FormData(form);
        const payload: any = {
            content_id: session_data?.content_id,
            answers: {}
        };

        formData.forEach((value, key) => {
            payload.answers[key] = value;
        });

        console.log(payload);
    };

    if (error) {
        toast.error(error, { position: 'top-right', });
        dispatch(clearError());
    }

    let allowAnswer = false;
    if (session_data && session_data.question) {
        if (session_data.question.type === 'select') {
            const question = session_data.question as QuestionTypeSelect;
            allowAnswer = question.options.some(option => option.selected);
        } else if (session_data.question.type === 'order') {
            const question = session_data.question as QuestionTypeOrder;
            allowAnswer = question.options.some(option => option.index !== null);
        } else if (session_data.question.type === 'connect') {
            const question = session_data.question as QuestionTypeConnect;
            allowAnswer = question.selected_pairs.length === question.options1.length;
        } else if (session_data.question.type === 'text') {
            const question = session_data.question as QuestionTypeText;
            allowAnswer = question.options.every(item => {
                const isRequired = Boolean(item.required); // Convert to boolean
                const hasValue = item.value !== null && item.value !== undefined && item.value !== ''; // Ensure value is not empty
                return !isRequired || (isRequired && hasValue);
            });
        }
    }


    let content;
    let backBtn = (
        <div className="circle-button">
            <div className="triangle-left"></div>
        </div>
    );
    let nextBtn = (
        <div className="circle-button">
            <div className="triangle-right"></div>
        </div>
    );
    let circleProgress = null;
    if (typeof session_data !== 'undefined') {
        circleProgress = <CircleProgress session_data={session_data} showPointsDiff={showPointsDiff} lang={lang} />;
    }
    if (session_data && session_data.question) {
        if (!btnDisabled) {
            backBtn = (
                <div className="circle-button active" onClick={() => handleBack()}>
                    <div className="triangle-left"></div>
                </div>
            );
        }
        if (!btnDisabled && allowAnswer) {
            if (session_data.question.answered) {
                nextBtn = (
                    <div className="circle-button pulse active" onClick={() => handleContinue()}>
                        <div className="triangle-right"></div>
                    </div>
                );
            } else {
                nextBtn = (
                    <div className={nextBtnClassName} onClick={() => handleAnswer()}>
                        <div className="triangle-right"></div>
                    </div>
                );
            }
        }
        content = (
            <div
                className={`game-container ${session_data && session_data.question && session_data.question?.answered ? 'answered' : ''}`}>
                <div className='game-card-header'>
                    <div className='container-fluid p-3 pb-2'>
                        <div className='row'>
                            <div className='col-12 d-flex flex-column align-items-center'>
                                <h3>{session_data.question.title}</h3>
                                <div className='description-wrapper'>
                                    <div className='description-text'
                                        dangerouslySetInnerHTML={{ __html: session_data.question.description }}>
                                    </div>
                                    {questionInfo && (
                                        <div className='question-info'>
                                            <InfoIcon />
                                            <span>{questionInfo}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='game-card-body'>
                    <QuestionContent question={session_data.question} lang={lang}
                        showPoints={session_data.points !== null} />
                    {session_data.question.outro_text &&
                    <div className='container-fluid pb-3'>
                        <div className='row'>
                            <div className='col-12 col-md-6 offset-md-3 text-center'>
                                <div className={`outro-text ${session_data.question.outro_text ? 'outro-visible' : ''}`}
                                    dangerouslySetInnerHTML={{ __html: session_data.question.outro_text }}>
                                </div>
                            </div>
                        </div>
                    </div>
                    }
                </div>
                <div className='glowing-bar'></div>
            </div>
        );
    } else if (session_data && session_data.end) {
        backBtn = (
            <div className="circle-button active" onClick={() => handleBack()}>
                <div className="triangle-left"></div>
            </div>
        );
        circleProgress = null;
        nextBtn = (<div></div>);
        content = (
            <div className='game-container game-end-screen'>
                <div className='game-card-header p-3'>
                    <div className='container-fluid'>
                        <div className='row'>
                            <div className='col-12 d-flex flex-column align-items-center'>
                                <h1>{session_data.end.title}</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='game-card-body'>
                    <div className='container-fluid'>
                        <div className='row'>
                            <div className='col-12 d-flex flex-column align-items-center'>
                                <p className='game-end-points-message'>{session_data.end.points_message}</p>
                                {session_data.points !== null && session_data.max_points > 0 &&
                                    <div className='game-end-points'>
                                        <h3>{LANG.points_scored}:</h3>
                                        <div className='points-container'>
                                            <span className='points-scored'>{session_data.points}</span> <span
                                                className='points-separator'>/</span> <span
                                                    className='max-points'>{session_data.max_points}</span>
                                        </div>
                                    </div>
                                }
                                {session_data.end.average_points !== null && session_data.max_points !== null && session_data.max_points > 0 &&
                                    <div className='game-end-average'>
                                        <h3>{LANG.all_players_average}:</h3>
                                        <div className='points-container'>
                                            <span className='average-points'>{session_data.end.average_points}</span>
                                        </div>
                                    </div>
                                }
                                {session_data.end.end_message !== "" &&
                                    <div className='game-end-message p-3' dangerouslySetInnerHTML={{ __html: session_data.end.end_message }} />
                                }
                            </div>
                            {session_data.end.form && session_data.end.form.length > 0 &&
                                <div className='col-12 col-md-8 offset-md-2 pb-3'>
                                    <form onSubmit={handleEndFormSubmit} id='game-end-form' className='row pb-4 gy-2'
                                          method="POST">
                                        <FormFields form={session_data.end.form}
                                                    handleCheckboxChange={handleCheckboxChange}
                                                    handleInputChange={handleInputChange}
                                                    onKeyDown={onKeyDown}/>
                                        <div className='col-12 d-flex justify-content-center pt-4'>
                                            <button type='submit' className='btn game-btn'>{LANG.submit}</button>
                                        </div>
                                    </form>
                                </div>
                            }
                        </div>
                    </div>
                </div>
                {/*<div className='glowing-bar'></div>*/}
                <div className='game-card-footer'>
                    <div className='container-fluid'>
                        <div className='row'>
                            <div className='col-12 d-flex justify-content-center pt-5'>
                                <button className='btn game-btn'
                                    onClick={() => dispatch(endGameSession())}>{LANG.quit_game}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    } else if (session_data && session_data.slide) {
        content = (
            <GameSlide btnDisabled={btnDisabled} slide={session_data.slide} lang={lang} handleAnswer={handleAnswer} />)
        if (!btnDisabled) {
            backBtn = (
                <div className="circle-button active" onClick={() => handleBack()}>
                    <div className="triangle-left"></div>
                </div>
            );
            nextBtn = (
                <div className="circle-button pulse active" onClick={() => handleAnswer()}>
                    <div className="triangle-right"></div>
                </div>
            );
        }
    } else {
        content = '';
    }

    // Disable back button on first page
    if (session_data && session_data.number === 1) {
        backBtn = (
            <div className="circle-button">
                <div className="triangle-left"></div>
            </div>
        );
    }

    return (
        <div className='container-fluid'>
            <CSSTransition nodeRef={transRef} in={isVisible} unmountOnExit timeout={TRANSITION_DURATION}
                classNames={slideTransition}>
                <div ref={transRef}>
                    {content}
                </div>
            </CSSTransition>
            <div className='game-shader'></div>
            <div className='game-bottom-bar'>
                {backBtn}
                {circleProgress}
                {nextBtn}
            </div>
        </div>
    )
}

export default GameSession;